// Front-end mirror of config/plans.php. The backend is authoritative and sends
// `plan_capabilities` in the user payload; this local map is only a fallback for
// older cached user objects that predate the plan feature.
import { getCurrentUser } from "./auth";

export const PLAN_CAPABILITIES = {
    free: {
        label: "Free",
        tickets_per_month: 500,
        qr_type: "fixed",
        statistics: "basic",
        max_services: 2,
        max_menu_links: 1,
        ads: true,
        text_editor: false,
    },
    plus: {
        label: "Plus",
        tickets_per_month: 1000,
        qr_type: "dynamic",
        statistics: "detailed",
        max_services: 5,
        max_menu_links: 3,
        ads: true,
        text_editor: true,
    },
    premium: {
        label: "Premium",
        tickets_per_month: 2000,
        qr_type: "dynamic",
        statistics: "advanced",
        max_services: null,
        max_menu_links: null,
        ads: false,
        text_editor: true,
    },
    enterprise: {
        label: "Enterprise",
        tickets_per_month: null,
        qr_type: "dynamic",
        statistics: "advanced",
        max_services: null,
        max_menu_links: null,
        ads: false,
        text_editor: true,
    },
};

const DEFAULT_PLAN = "free";

// Resolve the capability map for a user object (or the current user if omitted).
// Prefers the backend-sent `plan_capabilities`, falls back to the local map.
export const getPlanCapabilities = (user) => {
    const u = user || getCurrentUser();
    if (u?.plan_capabilities && typeof u.plan_capabilities === "object") {
        return u.plan_capabilities;
    }
    const plan = (u?.plan || DEFAULT_PLAN).toLowerCase();
    return PLAN_CAPABILITIES[plan] || PLAN_CAPABILITIES[DEFAULT_PLAN];
};

// Boolean feature check (e.g. "text_editor", "ads").
export const planCan = (feature, user) => {
    return Boolean(getPlanCapabilities(user)?.[feature]);
};

// Numeric limit (null = unlimited).
export const planLimit = (key, user) => {
    const value = getPlanCapabilities(user)?.[key];
    return value === undefined ? null : value;
};

// Whether a numeric limit permits `count` more existing items to grow.
export const withinLimit = (key, count, user) => {
    const limit = planLimit(key, user);
    if (limit === null || limit === undefined) return true; // unlimited
    return count < limit;
};

// Convenience helpers.
export const isFixedQr = (user) => getPlanCapabilities(user)?.qr_type === "fixed";
export const getPlanLabel = (user) => getPlanCapabilities(user)?.label || "Free";
